import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPatch, apiPost } from '../../lib/api';
// Starea actului vine din registru, cu schema lui: abonamentul o arată, nu o definește.
// Ca la `inbox.api.ts`, care citește la fel documentele — o a doua uniune aici ar fi rămas
// în urmă la prima stare nouă.
import { documentStateSchema } from '../documents/documents.api';

/**
 * Abonamentele, dinspre client.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/subscriptions`: `apps/crm`
 * e `scope:fe` și n-are voie să depindă de backend. Granița se validează în ambele capete,
 * iar un câmp redenumit pe server pică la `parse`, nu ajunge `undefined` în ecran.
 */

/**
 * Stările abonamentului. Numerele sunt cele din `crm_subscriptions.status`.
 *
 * Aici e **sursa de adevăr a interfeței**: eticheta stării, culoarea bulinei și cum se
 * numește acțiunea care duce în ea. Ecranul citește din tabelul ăsta în loc să înșire
 * `if`-uri.
 */
export const SUBSCRIPTION_STATUS = {
  pending: 0,
  active: 1,
  suspended: 2,
  terminated: 3,
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

export interface SubscriptionStatusDescriptor {
  readonly value: SubscriptionStatus;
  readonly label: string;
  readonly color: 'grey' | 'green' | 'orange' | 'red';
  /** Ce scrie pe butonul care duce **în** starea asta. */
  readonly actionLabel: string;
  /** Numele iconului din `@medusajs/icons` pentru acțiunea care duce în starea asta. */
  readonly actionIcon: 'clock' | 'play' | 'pause' | 'x';
  /** Dacă tranziția cere un motiv. Aceeași regulă ca pe server (`requiresReason`). */
  readonly needsReason: boolean;
  /**
   * Dacă tranziția cere o dată de reactivare. Aceeași regulă ca pe server
   * (`requiresResumeDate`): doar suspendarea. Activarea și rezilierea n-au termen.
   */
  readonly needsResumeDate: boolean;
}

export const SUBSCRIPTION_STATUS_LIST: readonly SubscriptionStatusDescriptor[] = [
  {
    value: SUBSCRIPTION_STATUS.pending,
    label: 'În așteptare semnare',
    color: 'grey',
    actionLabel: 'Pune în așteptare',
    actionIcon: 'clock',
    needsReason: false,
    needsResumeDate: false,
  },
  {
    value: SUBSCRIPTION_STATUS.active,
    label: 'Activ',
    color: 'green',
    actionLabel: 'Activează',
    actionIcon: 'play',
    needsReason: false,
    needsResumeDate: false,
  },
  {
    value: SUBSCRIPTION_STATUS.suspended,
    label: 'Suspendat',
    color: 'orange',
    actionLabel: 'Suspendă',
    actionIcon: 'pause',
    needsReason: true,
    needsResumeDate: true,
  },
  {
    value: SUBSCRIPTION_STATUS.terminated,
    label: 'Reziliat',
    color: 'red',
    actionLabel: 'Reziliază',
    actionIcon: 'x',
    needsReason: true,
    needsResumeDate: false,
  },
];

export function subscriptionStatusDescriptor(
  value: SubscriptionStatus,
): SubscriptionStatusDescriptor {
  return (
    SUBSCRIPTION_STATUS_LIST.find((status) => status.value === value) ?? SUBSCRIPTION_STATUS_LIST[0]
  );
}

/**
 * Ce tranziții propune ecranul, din fiecare stare.
 *
 * **Copie a regulii de pe server** (`domain/subscription-status.ts`), nu sursa ei: aici
 * decide ce butoane apar, acolo decide ce se salvează. Serverul rămâne singurul care
 * refuză — un buton ascuns nu apără nimic, iar cine ține pagina deschisă în timp ce
 * altcineva schimbă starea primește `409`, cu mesajul lui.
 */
const NEXT_STATUSES: Record<SubscriptionStatus, readonly SubscriptionStatus[]> = {
  [SUBSCRIPTION_STATUS.pending]: [SUBSCRIPTION_STATUS.active, SUBSCRIPTION_STATUS.terminated],
  [SUBSCRIPTION_STATUS.active]: [SUBSCRIPTION_STATUS.suspended, SUBSCRIPTION_STATUS.terminated],
  [SUBSCRIPTION_STATUS.suspended]: [SUBSCRIPTION_STATUS.active, SUBSCRIPTION_STATUS.terminated],
  [SUBSCRIPTION_STATUS.terminated]: [],
};

export function nextStatuses(from: SubscriptionStatus): readonly SubscriptionStatus[] {
  return NEXT_STATUSES[from];
}

/** Cât e în starea `0`, abonamentul se editează liber. După activare, cere anexă nouă. */
export function isEditable(status: SubscriptionStatus): boolean {
  return status === SUBSCRIPTION_STATUS.pending;
}

const statusSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);

const subscriptionSchema = z.object({
  id: z.uuid(),
  customerId: z.uuid(),
  packageId: z.uuid(),
  /** Adresa de instalare, pe un rând. `null` = serviciu fără adresă (colocare la noi). */
  address: z.string().nullable(),
  name: z.string(),
  /**
   * String, ca `costDefault`: coloana e `DECIMAL` și nu trece prin `number`.
   *
   * `null` = serviciul n-are preț (#66). Diferit de `'0.00'`, care e gratuit cu preț
   * cunoscut, și de câmpul gol, pe care serverul îl refuză în continuare.
   */
  cost: z.string().nullable(),
  currency: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  /** Dacă `cost` e cu TVA inclus. Se prepopulează din pachet, dar e a acestui client. */
  priceIncludesVat: z.boolean(),
  status: statusSchema,
  startDate: z.string().nullable(),
  expireDate: z.string().nullable(),
  /**
   * JSON **opac**, scris de sistemul extern. Se afișează ca text brut, nu se parsează:
   * l-am ține în ecran doar ca să-l redesenăm, iar prima cheie afișată frumos ar deveni o
   * cheie de care depindem.
   */
  subscriptionInfo: z.string().nullable(),
  createdAt: z.string(),
});

export type Subscription = z.infer<typeof subscriptionSchema>;

const subscriptionListItemSchema = subscriptionSchema.extend({
  packageName: z.string(),
  packageTypeName: z.string(),
  /**
   * Iconul tipului de serviciu, trimis deja normalizat de server. `z.string()`, nu
   * `z.enum(...)`: un nume pe care frontendul ăsta nu-l știe încă trebuie să cadă pe iconul
   * neutru, nu să facă lista de abonamente a clientului să pice la `parse`.
   */
  packageTypeIcon: z.string(),
  /**
   * Până când e suspendat abonamentul, de pe ultima lui tranziție. `null` pe orice altă
   * stare — și pe suspendările date înainte de #75, care n-au avut termen.
   */
  resumesAt: z.string().nullable(),
  /**
   * **Actul care a adus abonamentul în starea curentă** — ultima tranziție cu document.
   *
   * `null` cât nu s-a semnat nimic, sau dacă ciorna a fost ștearsă între timp (#142). Meniul
   * rândului **ascunde** atunci „Vezi actul": un rând gri ar sugera lipsă de drepturi, când
   * de fapt nu există hârtie.
   */
  documentId: z.uuid().nullable(),
});

export type SubscriptionListItem = z.infer<typeof subscriptionListItemSchema>;

const historyItemSchema = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  status: statusSchema,
  changedAt: z.string(),
  reason: z.string().nullable(),
  changedByName: z.string().nullable(),
  /** Doar pe rândurile care suspendă. Informativă: nimic nu se reactivează singur. */
  resumesAt: z.string().nullable(),
  /**
   * Actul care a produs tranziția, sau `null` — suspendarea și rezilierea făcute din
   * interfață au motiv și autor, nu document.
   */
  documentId: z.uuid().nullable(),
  /** `null` și când rândul n-are act, și când actul e **ciornă**, fără număr (#142). */
  documentNumber: z.number().int().nullable(),
  /** Starea actului **azi**: un act anulat sau reziliat rămâne proba perioadei lui. */
  documentState: documentStateSchema.nullable(),
});

export type SubscriptionHistoryItem = z.infer<typeof historyItemSchema>;

export const subscriptionsQueryKey = (customerId: string) => ['subscriptions', customerId] as const;

export const subscriptionHistoryQueryKey = (subscriptionId: string) =>
  ['subscriptions', 'history', subscriptionId] as const;

export const subscriptionsQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: subscriptionsQueryKey(customerId),
    queryFn: () =>
      apiGet(
        `/subscriptions?customerId=${customerId}`,
        z.object({ items: z.array(subscriptionListItemSchema) }),
      ).then((body) => body.items),
  });

export const subscriptionHistoryQueryOptions = (subscriptionId: string | null) =>
  queryOptions({
    queryKey: subscriptionHistoryQueryKey(subscriptionId ?? ''),
    // `enabled` în loc de o ramură în componentă: sertarul de istoric se montează închis,
    // iar o interogare pornită fără abonament ar cere `/subscriptions//history`.
    enabled: subscriptionId !== null,
    queryFn: () =>
      apiGet(
        `/subscriptions/${subscriptionId}/history`,
        z.object({ history: z.array(historyItemSchema) }),
      ).then((body) => body.history),
  });

/**
 * Ce trimite formularul.
 *
 * **Totul e text**, inclusiv prețul: un `<input>` gol e `''`, nu `null`, iar contractul de
 * pe server știe deja să facă diferența. Normalizarea sumei („49,99" → „49.99") e a
 * serverului — o a doua implementare în browser ar putea da alt verdict decât cea care
 * contează.
 *
 * Singura excepție e `cost`, care poate fi și `null` — dar numai ca **declarație**, din
 * comutatorul „Fără preț", niciodată din câmp gol.
 */
export interface SubscriptionInput {
  readonly packageId: string;
  readonly address: string;
  readonly name: string;
  /**
   * `null` **doar** când operatorul a bifat „Fără preț".
   *
   * Câmpul gol rămâne `''` și pleacă așa spre server, care îl refuză: „am uitat prețul" nu
   * trebuie să producă același rând ca „serviciul n-are preț".
   */
  readonly cost: string | null;
  readonly currency: 1 | 2 | 3;
  readonly priceIncludesVat: boolean;
  readonly startDate: string;
  /**
   * **Durata, în luni** — nu data de expirare.
   *
   * Text, ca tot ce iese dintr-un `<input>`; numărul se face la trimitere. Data de expirare
   * o calculează **serverul**, din `startDate` și atât: două surse de adevăr pentru data de
   * pe un act semnat înseamnă că într-o zi ele diferă, iar cea greșită e deja tipărită.
   * Formularul o arată, cu aceeași funcție (`@pcrm/shared/period`), ca s-o vadă operatorul
   * înainte de a salva.
   */
  readonly months: string;
}

/**
 * Ce pleacă pe rețea.
 *
 * Singura conversie din formular: durata devine număr, pentru că serverul o cere `z.number()`.
 * `null` când câmpul e gol sau nu e un întreg — serverul răspunde atunci cu mesajul lui, sub
 * câmp, în loc să primească un `NaN` pe care JSON îl face oricum `null`.
 */
function toPayload(input: SubscriptionInput) {
  const months = /^\d+$/.test(input.months.trim()) ? Number(input.months) : null;
  return { ...input, months };
}

export function createSubscription(
  customerId: string,
  input: SubscriptionInput,
): Promise<Subscription> {
  return apiPost(
    '/subscriptions',
    { ...toPayload(input), customerId },
    z.object({ subscription: subscriptionSchema }),
  ).then((body) => body.subscription);
}

export function updateSubscription(id: string, input: SubscriptionInput): Promise<Subscription> {
  return apiPatch(
    `/subscriptions/${id}`,
    toPayload(input),
    z.object({ subscription: subscriptionSchema }),
  ).then((body) => body.subscription);
}

/**
 * Schimbarea de stare.
 *
 * `resumesAt` pleacă **doar la suspendare**: la activare și la reziliere nu există termen,
 * iar o dată rămasă în formular n-are ce căuta pe rândul lor de istoric. Serverul o ignoră
 * oricum acolo — filtrul de aici e ca cererea să spună ce vrea ecranul, nu ca să apere.
 */
export function changeSubscriptionStatus(
  id: string,
  status: SubscriptionStatus,
  reason: string,
  resumesAt: string,
): Promise<Subscription> {
  return apiPost(
    `/subscriptions/${id}/status`,
    {
      status,
      reason,
      resumesAt: status === SUBSCRIPTION_STATUS.suspended ? resumesAt : '',
    },
    z.object({ subscription: subscriptionSchema }),
  ).then((body) => body.subscription);
}
